/**
 * One fixed tick. Order per 04 §4: phase → players (inputs, movement) →
 * discs (contacts collected, sorted by sweep time) → eliminations applied
 * in a batch → catches/pickups → despawn sweep → round-end check.
 *
 * Pure: `step` never mutates its input. Deterministic: no wall clock, no
 * Math.random, players/discs iterate in id order.
 */
import { resolveCatches, resolvePickups, stepDiscs, sweepDespawned } from './disc.js'
import { stepPlayer } from './player.js'
import { applyEliminations, beginTick, endTick, type TickPermissions } from './round.js'
import { cloneState } from './state.js'
import type { InputMap, PendingHit, SimState } from './types.js'

export function step(state: SimState, inputs: InputMap, dt: number): SimState {
  const s = cloneState(state)
  stepInPlace(s, inputs, dt)
  return s
}

/** Returns the tick's permissions so a caller (the client predictor) can tell whether inputs were applied this tick. */
export function stepInPlace(s: SimState, inputs: InputMap, dt: number): TickPermissions {
  s.events.length = 0
  s.tick += 1
  const perm = beginTick(s)
  if (perm.frozen) return perm

  for (const p of s.players) stepPlayer(s, p, inputs.get(p.id), dt, perm)

  const hits: PendingHit[] = []
  stepDiscs(s, dt, hits, perm.combat)
  hits.sort((a, b) => a.sweepT - b.sweepT || a.discId - b.discId)
  applyEliminations(s, hits)

  resolveCatches(s)
  resolvePickups(s)
  sweepDespawned(s)
  endTick(s)
  return perm
}
